/**
 * What the offline queue's flushes say, in toasts — held while the PIN lock is
 * up (FM Technician B17, review R2-2).
 *
 * The queue sends in the background, lock or no lock, and used to toast
 * straight away: "2 saved items sent", "Not accepted: … (Finish — WO/…)" sat
 * ON TOP of the lock screen, readable by whoever picked the phone up
 * (interpretation 160: nothing readable behind — or over — the lock). Now a
 * flush that ends while the signed-in person is locked is kept here and told
 * after the unlock. Nothing is lost by waiting: the refusals stay in
 * takeRejected until read, and the blocked list keeps them for good.
 *
 * 🔴 HELD FOR ONE PERSON (review R2-3 #6). What is held belongs to whoever was
 * signed in and locked when it was held. It is told only to that same person,
 * once their PIN unlocks — never on the login screen after the lock's password
 * way out, and never to the next person who signs in on the phone. Signing out
 * drops it (dropHeldQueueToasts, from the auth store): the blocked list keeps
 * every refusal, so the person finds it in Unsent work when they are back.
 */
import toast from 'react-hot-toast';
import { getSignedInUserId } from '@/utils/api';
import { isLocked, onLockChange } from '@/components/Tech/lockState';
import { takeRejected, type FlushResult } from './pendingQueue';

let heldSent = 0;
let held = false;
/** Whose flushes are held — the person signed in and locked at the time. */
let heldFor: string | null = null;

function show(sent: number, uid: string): void {
  if (sent > 0) toast.success(`${sent} saved item${sent === 1 ? '' : 's'} sent`);
  // Only this person's refusals (review R2-4 #2); anyone else's are dropped —
  // the blocked list keeps them under their owner.
  for (const item of takeRejected(uid)) {
    toast.error(`Not accepted: ${item.lastError || 'refused'} (${item.label})`, { duration: 12_000 });
  }
}

const lockedNow = () => isLocked(getSignedInUserId());

/** Hand every flush result here (initPendingQueue's callback). */
export function reportFlush(r: FlushResult): void {
  const uid = getSignedInUserId();
  // Signed out by the time the flush ended: nobody to tell, and the login
  // screen is not the place (R2-3 #6). Refusals stay in the blocked list.
  if (!uid) {
    dropHeldQueueToasts();
    return;
  }
  // 🔴 A flush of someone else's list (review R2-4 #2): person A's flush was
  // out when A signed out and B signed in, and it ended under B. Its count
  // and its refusals are A's — never told to B. Dropped here; A's refusals
  // stay in A's blocked list, and show() drains only the signed-in person's.
  if (r.uid !== uid) return;
  // Something held for someone else is not this person's to see.
  if (held && heldFor !== uid) dropHeldQueueToasts();
  if (lockedNow()) {
    heldSent += r.sent;
    held = true;
    heldFor = uid;
    return;
  }
  const n = heldSent + r.sent;
  heldSent = 0;
  held = false;
  heldFor = null;
  show(n, uid);
}

/**
 * Forget what is held, and the refusals not yet toasted (R2-3 #6). Called on
 * sign-out BEFORE the lock is cleared — clearing it is a lock change, and the
 * listener below must find nothing to show. The refusals are not lost: the
 * blocked list keeps each one under its owner's id.
 */
export function dropHeldQueueToasts(): void {
  heldSent = 0;
  held = false;
  heldFor = null;
  takeRejected();
}

/** Tell what was held as soon as the lock comes down. Returns a stop. */
export function startQueueToasts(): () => void {
  return onLockChange(() => {
    if (!held || lockedNow()) return;
    // Only to the person it was held for, still signed in — unlocked by
    // their PIN, not signed out (or replaced) under the lock.
    if (getSignedInUserId() !== heldFor) return;
    const n = heldSent;
    heldSent = 0;
    held = false;
    const owner = heldFor;
    heldFor = null;
    if (owner) show(n, owner);
  });
}
